import { Download } from 'lucide-react';
import { type ComponentProps, type ReactNode, useState } from 'react';
import { useTranslate } from 'ra-core';
import {
  type SpreadsheetColumns,
  saveSpreadsheet,
} from '../../utils/spreadsheet/save';
import { type CsvRecord } from './types';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export interface ExportSheetButtonProps<T extends CsvRecord> {
  label: string;
  icon?: ReactNode;
  variant?: ComponentProps<typeof Button>['variant'];
  getSheet: () => {
    data?: T[];
    columns?: SpreadsheetColumns<T>;
    basename: string;
  };
}

export function ExportSheetButton<T extends CsvRecord>({
  label,
  icon,
  variant = 'outline',
  getSheet,
}: ExportSheetButtonProps<T>) {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const translatedLabel = translate(label, { _: label });

  const downloadSheet = (format: 'excel' | 'csv') => {
    const sheet = getSheet();

    void saveSpreadsheet<T>({
      format: format === 'excel' ? 'xlsx' : 'csv',
      data: sheet.data ?? [],
      basename: sheet.basename,
      columns: sheet.columns,
    });

    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        render={
          <Button type="button" variant={variant}>
            {icon ?? <Download />}
            {translatedLabel}
          </Button>
        }
      />
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => downloadSheet('excel')}>
          Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => downloadSheet('csv')}>
          CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
